import axios from "axios";
import { apiPath } from "../utils/apiClient";

function isApprovedStatus(status) {
  return String(status || "").toLowerCase() === "approved";
}

function isCloudinaryUrl(url) {
  return typeof url === "string" && url.startsWith("https://res.cloudinary.com/");
}

function normalizeInsightsList(data) {
  return Array.isArray(data) ? data : (data?.insights || []);
}

/** Static JSON lives on GoDaddy — must use fetch(), not axios (axios.baseURL points at Render API). */
async function fetchStaticJson(filename) {
  const url = `/static-data/${filename}`;
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Failed to load ${url}: HTTP ${res.status}`);
  }
  return res.json();
}

async function loadStaticInsights() {
  const data = await fetchStaticJson("insights.json");
  const insights = normalizeInsightsList(data).filter(
    (i) => isApprovedStatus(i.status) && isCloudinaryUrl(i.image)
  );
  return {
    insights,
    source: "inside-static",
    cloudName: data?.cloudName || "drhjytbbs",
  };
}

async function loadStaticGallery() {
  const data = await fetchStaticJson("gallery.json");
  const images = data?.images || [];
  return {
    images,
    source: "gallery-static",
    cloudName: data?.cloudName || "djdgvpeez",
  };
}

export async function fetchGalleryPhotos() {
  const url = apiPath("/api/photos");
  try {
    console.log("[Gallery] Fetching from:", url);
    const response = await axios.get(url, {
      timeout: 90000,
      withCredentials: false,
    });
    const photosData = response.data;
    const images = Array.isArray(photosData)
      ? photosData
      : (photosData.images || photosData.photos || []);
    const source = photosData.source || "gallery-api";
    const cloudName = photosData.cloudName;

    if (photosData.error && images.length === 0) {
      console.error("[Gallery] API error:", photosData.error, photosData.details);
      throw new Error(photosData.details || photosData.error);
    }

    if (images.length > 0) {
      console.log(
        `[Gallery] Loaded ${images.length} images (source: ${source}${cloudName ? `, cloud: ${cloudName}` : ""})`
      );
      return { images, source, cloudName };
    }
    throw new Error("No gallery images from API");
  } catch (err) {
    const message = err.response?.data?.details || err.response?.data?.error || err.message;
    console.error("[Gallery] API fetch failed, trying static cache:", message);
    try {
      const staticResult = await loadStaticGallery();
      if (staticResult.images.length > 0) {
        console.log(`[Gallery] Loaded ${staticResult.images.length} images from static cache`);
        return staticResult;
      }
    } catch {
      /* no static fallback */
    }
    throw new Error(message || "Failed to load gallery from Gallery Cloudinary API");
  }
}

export async function fetchApprovedInsights() {
  const url = apiPath("/api/insights/approved");
  try {
    console.log("[Inside] Fetching approved insights from:", url);
    const response = await axios.get(url, {
      timeout: 90000,
      withCredentials: false,
    });
    const insights = normalizeInsightsList(response.data).filter(
      (i) => isApprovedStatus(i.status) && isCloudinaryUrl(i.image)
    );
    if (insights.length > 0) {
      console.log(`[Inside] Loaded ${insights.length} approved insights from API`);
      return {
        insights,
        source: "inside-api",
        cloudName: response.data?.cloudName || "drhjytbbs",
      };
    }
    console.warn("[Inside] API returned no Cloudinary insights, trying static bundle");
  } catch (error) {
    console.error("[Inside] API fetch failed, trying static bundle:", error.message);
  }

  try {
    const staticResult = await loadStaticInsights();
    if (staticResult.insights.length > 0) {
      console.log(`[Inside] Loaded ${staticResult.insights.length} insights from static bundle`);
      return staticResult;
    }
  } catch (staticErr) {
    console.error("[Inside] Static insights fallback failed:", staticErr.message);
  }

  return { insights: [], source: "none" };
}

export async function fetchInsightDetail(id) {
  if (!id) throw new Error("Insight ID is required");

  const url = apiPath(`/api/insights/${id}`);
  try {
    const response = await axios.get(url, { timeout: 90000, withCredentials: false });
    if (response.data && (response.data._id || response.data.title)) {
      console.log(`[Inside] Loaded insight "${response.data.title}" from API`);
      return { insight: response.data, source: "inside-api" };
    }
  } catch (error) {
    console.error("[Inside] Detail API failed, trying static bundle:", error.message);
  }

  try {
    const data = await fetchStaticJson("insights.json");
    const insights = normalizeInsightsList(data);
    const found = insights.find((i) => String(i._id) === String(id));
    if (found && isApprovedStatus(found.status)) {
      console.log(`[Inside] Loaded insight "${found.title}" from static bundle`);
      return { insight: found, source: "inside-static" };
    }
  } catch (staticErr) {
    console.error("[Inside] Static detail lookup failed:", staticErr.message);
  }

  throw new Error("Insight not found or not published yet.");
}
